/**
 * Payments — money overview, CLEAN + MANUAL (owner directive 2026-06-13).
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  WHY THIS CHANGED (2026-06-13 — owner rejected the auto-aggregate)
 * ─────────────────────────────────────────────────────────────────────────
 *  The foundation slice auto-aggregated EVERY live project (602 of them,
 *  mostly "ZZ TEST" / "safe to delete" / unpriced placeholders) into a
 *  misleading "₹46,75,000 outstanding · 602 projects" total. The owner:
 *    "remove all the data and allow me to add it manually, payment-style,
 *     one by one. ₹46,75,000 outstanding for 602 projects is not true."
 *
 *  NEW MODEL — Payments is now MANUAL:
 *    • The overview / ₹-$ split / firm rollup reflect ONLY the engagements the
 *      owner has explicitly TRACKED (`project.trackedInPayments === true`).
 *      Payments starts effectively EMPTY — no assumed money across the legacy
 *      dump.
 *    • "Add an engagement to Payments" lets the owner build it up one-by-one:
 *      pick a real project → set its currency + value + firm (which prices it
 *      AND tracks it via `stampProjectMoney`, or just track an already-priced
 *      project). Obvious junk ("ZZ TEST", "safe to delete", unpriced
 *      placeholders) is hidden from the picker by default (`isLikelyTestProject`),
 *      with a "show all" escape.
 *    • Each tracked row can be removed from Payments (`setProjectTrackedInPayments`)
 *      — the project + its data are NEVER deleted, it just stops surfacing as money.
 *
 *  Once the owner enters a $ engagement, a real ₹/$ separation appears and the
 *  totals become TRUE. Recording a payment (record-payment slice) is unchanged.
 *
 *  This is still a READ / VISUALISE surface for the totals; the SOLE release
 *  authority stays `recheckGate` / `projects/{id}.gateState.moneyPaid` (server).
 *  Admin-gated route + the screen re-asserts `seesMoneyInternals`.
 * ─────────────────────────────────────────────────────────────────────────
 */
import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../lib/auth';
import { useToast } from '../components/Toast';
import { visibilityFor } from '../lib/roles';
import { Box, Btn3, Card, EmptyNote, Tag } from '../components/ui';
import {
  listenProjects,
  isLikelyTestProject,
  projectLabel,
  type LiveProject,
} from '../lib/projectStore';
import {
  moneyOverview,
  moneyProjectFromLive,
  money,
  inr,
  searchTrackedEngagements,
  type MoneyOverview,
  type ProjectMoney,
} from '../lib/moneyModel';
import { ALL_FIRMS, type Currency } from '../lib/firms';
import RecordPaymentFlow from '../components/RecordPaymentFlow';
import AgreedPricePanel from '../components/AgreedPricePanel';
import {
  stampProjectMoney,
  setProjectTrackedInPayments,
  loadSettledTotals,
  type SettledTotals,
} from '../lib/recordPaymentStore';
import {
  listenCashBook,
  searchCashBook,
  updateCashBookEntry,
  type CashBookEntry,
} from '../lib/cashBook';
import { methodsFor, methodLabel, type UiMethod } from '../lib/recordPayment';

export default function Money() {
  const { role, user } = useAuth();
  const vis = visibilityFor(role);
  const actorName = user?.displayName || user?.email || 'Admin';

  const [projects, setProjects] = useState<LiveProject[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  /** When set, the record-payment flow is open (optionally pre-scoped). */
  const [recordFor, setRecordFor] = useState<{ pid?: string } | null>(null);
  /** When true, the "Add an engagement to Payments" picker is open. */
  const [addOpen, setAddOpen] = useState(false);
  /**
   * UX C (2026-06-16) — the engagement list is a COMPACT clickable shortlist;
   * clicking a row opens ITS detail (agreed price + change history + gates +
   * record payment). `openPid` = the engagement whose detail is expanded
   * (null = list only). Keeps the dashboard scannable for many engagements.
   */
  const [openPid, setOpenPid] = useState<string | null>(null);
  /**
   * SCALE FIX (2026-06-16) — the tracked-engagement list was an uncapped
   * stacked column that grew the page unbounded. `engSearch` filters the
   * ALREADY-tracked list (VP / client / C-number / project) — distinct from
   * the Add-picker search (which searches UNtracked projects). The list now
   * caps at ~4 visible rows and scrolls inside its own box.
   */
  const [engSearch, setEngSearch] = useState('');
  /** Foreign-settlement rollups (INR actually banked) per tracked project. */
  const [settled, setSettled] = useState<Record<string, SettledTotals>>({});

  useEffect(() => {
    if (!vis.seesMoneyInternals) return;
    const unsub = listenProjects(
      (rows) => setProjects(rows),
      {
        onErr: (e) => setErr(e.message),
        onReady: () => setProjects((p) => p ?? []),
      },
    );
    return () => unsub();
  }, [vis.seesMoneyInternals]);

  // CLEAN MANUAL PAYMENTS — only OWNER-TRACKED engagements feed the figures.
  const tracked = useMemo(
    () => (projects ?? []).filter((p) => p.trackedInPayments),
    [projects],
  );

  // Load the INR-banked rollup for tracked USD engagements (the "received in
  // USD, settled in INR" picture). INR engagements have no settlement, so we
  // only query the USD ones. Re-runs when the set of USD pids changes.
  const usdPidsKey = useMemo(
    () =>
      tracked
        .filter((p) => p.currency === 'USD')
        .map((p) => p.pid)
        .sort()
        .join(','),
    [tracked],
  );
  useEffect(() => {
    if (!usdPidsKey) {
      setSettled({});
      return;
    }
    let live = true;
    const pids = usdPidsKey.split(',');
    Promise.all(
      pids.map((pid) =>
        loadSettledTotals(pid)
          .then((t) => [pid, t] as const)
          .catch(() => [pid, { receivedUSD: 0, settledINR: 0, count: 0 }] as const),
      ),
    ).then((pairs) => {
      if (!live) return;
      setSettled(Object.fromEntries(pairs));
    });
    return () => {
      live = false;
    };
  }, [usdPidsKey]);

  // Total INR actually banked from USD receipts (across tracked engagements).
  const totalBankedINR = useMemo(
    () => Object.values(settled).reduce((s, t) => s + t.settledINR, 0),
    [settled],
  );

  if (!vis.seesMoneyInternals) {
    return (
      <Box title="Payments" note="Restricted">
        <EmptyNote title="Not available on this track">
          Money internals are admin-only.
        </EmptyNote>
      </Box>
    );
  }

  if (err) {
    return (
      <Box title="Payments" note="error">
        <EmptyNote title="Could not load projects">{err}</EmptyNote>
      </Box>
    );
  }

  if (projects === null) {
    return (
      <Box title="Payments" note="loading…">
        <EmptyNote title="Loading projects…">
          Resolving the engagements you track in Payments.
        </EmptyNote>
      </Box>
    );
  }

  const ov: MoneyOverview = moneyOverview(
    tracked.map((p) => moneyProjectFromLive(p)),
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* ── Entry actions ── */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 10,
          flexWrap: 'wrap',
        }}
      >
        <Btn3 intent="ink" onClick={() => setAddOpen(true)}>
          + Add an engagement to Payments
        </Btn3>
        {tracked.length > 0 && (
          <Btn3 intent="grn" onClick={() => setRecordFor({})}>
            + Record payment
          </Btn3>
        )}
      </div>

      {addOpen && (
        <AddEngagementPicker
          projects={projects}
          actorName={actorName}
          onClose={() => setAddOpen(false)}
        />
      )}

      {recordFor && tracked.length > 0 && (
        <RecordPaymentFlow
          projects={tracked}
          initialProjectId={recordFor.pid}
          onClose={() => setRecordFor(null)}
        />
      )}

      {/* ── Empty state — Payments starts clean ── */}
      {tracked.length === 0 ? (
        <Box
          title="Payments"
          note="clean · manual · one engagement at a time"
        >
          <EmptyNote title="No engagements tracked yet">
            Payments starts empty on purpose — it shows only the real
            engagements you choose to track, not every imported project. Tap{' '}
            <b>“+ Add an engagement to Payments”</b> to add your first one
            (set its currency, value and firm). The ₹ / $ totals and the
            per-firm split build up as you add real engagements.
          </EmptyNote>
        </Box>
      ) : (
        <>
          {/* ── OWNER REDESIGN (2026-07-03): the tracked-engagements block was
                 "far too big" — collapsed to ONE summary line (₹ and $ stay
                 separate, never summed) with the firm split folded to a second
                 compact line beneath it. The big card grids are gone. ── */}
          <Box
            title="Payments — tracked engagements"
            note="money-before-delivery · ₹ and $ separate · firm-routed"
          >
            {ov.byCurrency.length === 0 ? (
              <EmptyNote title="No priced engagements yet">
                The engagements you added are not yet priced. Set each one's
                currency &amp; value below so its payment schedule resolves.
              </EmptyNote>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {/* line 1 — the whole picture in one glance */}
                <div
                  data-testid="payments-summary-line"
                  style={{
                    display: 'flex',
                    gap: 14,
                    flexWrap: 'wrap',
                    alignItems: 'baseline',
                    fontSize: 13.5,
                    color: 'var(--ink-soft)',
                  }}
                >
                  {ov.byCurrency.map((t) => (
                    <span key={t.currency} style={{ whiteSpace: 'nowrap' }}>
                      <b style={{ color: 'var(--ink)' }}>{money(t.received, t.currency)}</b>{' '}
                      received ·{' '}
                      <b style={{ color: 'var(--warn)' }}>{money(t.balance, t.currency)}</b>{' '}
                      due{' '}
                      <span style={{ color: 'var(--ink-mute)' }}>
                        (of {money(t.value, t.currency)})
                      </span>
                      {t.currency === 'USD' && totalBankedINR > 0 && (
                        <span style={{ color: 'var(--ink-mute)' }}>
                          {' '}· {inr(totalBankedINR)} banked
                        </span>
                      )}
                    </span>
                  ))}
                  <span style={{ fontSize: 12, color: 'var(--ink-mute)' }}>
                    {tracked.length} engagement{tracked.length === 1 ? '' : 's'}
                    {ov.unpricedCount > 0 && ` · ${ov.unpricedCount} unpriced`}
                  </span>
                </div>
                {/* line 2 — the firm split, folded to one compact line */}
                {ov.byFirm.length > 0 && (
                  <div
                    data-testid="payments-firm-line"
                    style={{
                      display: 'flex',
                      gap: 14,
                      flexWrap: 'wrap',
                      alignItems: 'baseline',
                      fontSize: 12,
                      color: 'var(--ink-mute)',
                    }}
                  >
                    {ALL_FIRMS.map((firm) => {
                      const split = ov.byFirm.find((x) => x.firmId === firm.id);
                      if (!split) return null;
                      return (
                        <span key={firm.id} style={{ whiteSpace: 'nowrap' }}>
                          <FirmChip firmId={firm.id} label={firm.shortName} />{' '}
                          {split.byCurrency
                            .map(
                              (t) =>
                                `${money(t.received, t.currency)} in · ${money(t.balance, t.currency)} due`,
                            )
                            .join(' · ')}{' '}
                          ({split.projectCount})
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </Box>

          {/* ── CASH BOOK — every payment as a compact ledger row (owner
                 redesign 2026-07-03): date · engagement · what · amount ·
                 status; click a row to OPEN AND EDIT the entry (amount /
                 method / reference / INR banked). payments/ rules already
                 grant team write — no rules change. One page scroll. ── */}
          <CashBookLedger tracked={tracked} actorName={actorName} />

          {/* ── Per-engagement COMPACT shortlist → click a row to open detail
                 (UX C, 2026-06-16). The full card (agreed price + change
                 history + gates) is NO longer rendered inline for every
                 engagement — only for the one the owner clicks open. ── */}
          <Box
            title="Engagements"
            note="tap a row to open its agreed price, change history & payment gates"
          >
            {/* SCALE FIX (2026-06-16) — search the tracked list + cap ~4
                visible rows then scroll INSIDE the box (the page no longer
                grows unbounded). This search filters the ALREADY-tracked
                engagements; the "Add an engagement" picker above searches
                UNtracked projects — two different lists, two different searches. */}
            {ov.rows.length > 4 && (
              <input
                type="search"
                value={engSearch}
                onChange={(e) => setEngSearch(e.target.value)}
                placeholder="Search tracked engagements — VP, client, C-number, project…"
                aria-label="Search tracked engagements"
                data-testid="engagement-search"
                style={{
                  width: '100%',
                  padding: '9px 12px',
                  borderRadius: 'var(--r-sm)',
                  border: '1px solid var(--rule-strong)',
                  fontFamily: 'inherit',
                  fontSize: 14,
                  boxSizing: 'border-box',
                  marginBottom: 12,
                }}
              />
            )}
            {(() => {
              const filtered = searchTrackedEngagements(ov.rows, engSearch);
              if (filtered.length === 0) {
                return (
                  <EmptyNote title="No engagement matches">
                    Nothing matches “{engSearch}”. Clear the search to see all{' '}
                    {ov.rows.length} tracked engagements.
                  </EmptyNote>
                );
              }
              return (
                <div
                  data-testid="engagement-list"
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                    // OWNER HARD RULE (2026-07-03): no inner scrollbox — rows
                    // flow at natural height; the page owns the ONE scroll.
                    // The search above keeps long lists workable.
                  }}
                >
                  {filtered.map((m) => (
                    <EngagementListRow
                      key={m.pid}
                      m={m}
                      settled={settled[m.pid] ?? null}
                      open={openPid === m.pid}
                      onToggle={() =>
                        setOpenPid((cur) => (cur === m.pid ? null : m.pid))
                      }
                    />
                  ))}
                </div>
              );
            })()}
          </Box>

          {/* ── Detail for the opened engagement only ── */}
          {openPid &&
            (() => {
              const m = ov.rows.find((r) => r.pid === openPid);
              if (!m) return null;
              const live = tracked.find((p) => p.pid === m.pid) ?? null;
              return (
                <ProjectRow
                  key={m.pid}
                  m={m}
                  live={live}
                  actorName={actorName}
                  settled={settled[m.pid] ?? null}
                  onRecord={() => setRecordFor({ pid: m.pid })}
                  onCollapse={() => setOpenPid(null)}
                />
              );
            })()}
        </>
      )}
    </div>
  );
}

/* ════════════════════ Add-an-engagement picker ════════════════════ */

/**
 * The owner builds Payments up one engagement at a time. The picker lists
 * UNTRACKED projects (search + obvious-junk hidden by default), and tracking
 * one is as simple as a click — pricing it later (or here) makes its figures
 * exact. This is the "add it manually, payment-style, one by one" path.
 */
function AddEngagementPicker({
  projects,
  actorName,
  onClose,
}: {
  projects: LiveProject[];
  actorName: string;
  onClose: () => void;
}) {
  const { toast } = useToast();
  const [q, setQ] = useState('');
  const [showAll, setShowAll] = useState(false);
  const [busyPid, setBusyPid] = useState<string | null>(null);

  const candidates = useMemo(() => {
    const query = q.trim().toLowerCase();
    return projects
      .filter((p) => !p.trackedInPayments && !p.archived && !p.trashed)
      .filter((p) => showAll || !isLikelyTestProject(p))
      .filter((p) => {
        if (!query) return true;
        return `${projectLabel(p.projectName, p.vp, p.tier)}  ${p.clientName}  ${p.vp}  ${p.clientNo}`
          .toLowerCase()
          .includes(query);
      })
      .sort((a, b) => (a.vp < b.vp ? 1 : -1))
      .slice(0, 60);
  }, [projects, q, showAll]);

  async function track(p: LiveProject) {
    setBusyPid(p.pid);
    try {
      await setProjectTrackedInPayments(p.pid, true, actorName);
      toast(`Added ${p.vp} to Payments. Set its value to make figures exact.`, {
        ok: true,
      });
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Could not add to Payments.', {});
    } finally {
      setBusyPid(null);
    }
  }

  return (
    <Box
      title="Add an engagement to Payments"
      note="pick a real engagement — junk rows hidden by default"
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by project, client, VP or C number…"
            style={{
              flex: 1,
              minWidth: 220,
              padding: '9px 12px',
              borderRadius: 'var(--r-sm)',
              border: '1px solid var(--rule-strong)',
              fontFamily: 'inherit',
              fontSize: 14,
            }}
          />
          <label
            style={{
              fontSize: 12,
              color: 'var(--ink-soft)',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <input
              type="checkbox"
              checked={showAll}
              onChange={(e) => setShowAll(e.target.checked)}
            />
            Show test / placeholder rows too
          </label>
          <Btn3 intent="ghost" size="sm" onClick={onClose}>
            Done
          </Btn3>
        </div>

        {candidates.length === 0 ? (
          <EmptyNote title="Nothing to add">
            {q.trim()
              ? 'No untracked engagement matches your search.'
              : showAll
                ? 'Every project is already tracked in Payments.'
                : 'No real (non-test) untracked engagements found. Tick “show test / placeholder rows too” to see everything.'}
          </EmptyNote>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {candidates.map((p) => (
              <Card key={p.pid} flat className="row-card">
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    flexWrap: 'wrap',
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 13.5 }}>
                      {projectLabel(p.projectName, p.vp, p.tier)}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--ink-mute)' }}>
                      {p.clientName} · {p.vp}
                      {isLikelyTestProject(p) && (
                        <>
                          {' '}
                          <Tag kind="warn">looks like test</Tag>
                        </>
                      )}
                    </div>
                  </div>
                  <span style={{ marginLeft: 'auto' }}>
                    <Btn3
                      intent="ink"
                      size="sm"
                      disabled={busyPid === p.pid}
                      onClick={() => track(p)}
                    >
                      {busyPid === p.pid ? 'Adding…' : 'Add to Payments'}
                    </Btn3>
                  </span>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Box>
  );
}

function EngagementListRow({
  m,
  settled,
  open,
  onToggle,
}: {
  m: ProjectMoney;
  settled: SettledTotals | null;
  open: boolean;
  onToggle: () => void;
}) {
  const banked =
    m.currency === 'USD' && settled && settled.settledINR > 0 ? settled : null;
  return (
    <div
      className="row-card"
      onClick={onToggle}
      role="button"
      tabIndex={0}
      data-testid={`engagement-row-${m.pid}`}
      aria-expanded={open}
      onKeyDown={(e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onToggle();
        }
      }}
      style={{
        cursor: 'pointer',
        border: '1px solid var(--rule)',
        borderRadius: 'var(--r-sm)',
        padding: '12px 14px',
        background: open ? 'var(--inset)' : 'var(--surface)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          flexWrap: 'wrap',
        }}
      >
        <span aria-hidden style={{ color: 'var(--ink-mute)', fontSize: 12 }}>
          {open ? '▾' : '▸'}
        </span>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 13.5 }}>
            {m.vp} — {m.clientName}
          </div>
          <div
            style={{
              fontSize: 12,
              color: 'var(--ink-mute)',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              flexWrap: 'wrap',
              marginTop: 2,
            }}
          >
            <FirmChip firmId={m.firmId} label={m.firmShort} />
            <Tag kind="tag">{m.currency}</Tag>
            <span>value {money(m.valueINR, m.currency)}</span>
          </div>
        </div>
        <span
          style={{
            marginLeft: 'auto',
            fontSize: 13,
            color: 'var(--ink-soft)',
            textAlign: 'right',
          }}
          data-testid={`engagement-rollup-${m.pid}`}
        >
          {m.unpriced ? (
            <Tag kind="warn">not yet priced</Tag>
          ) : (
            <>
              <b style={{ color: 'var(--good)' }}>
                {money(m.receivedINR, m.currency)}
              </b>{' '}
              received ·{' '}
              <b style={{ color: 'var(--warn)' }}>
                {money(m.balanceINR, m.currency)}
              </b>{' '}
              outstanding
              {banked ? ` · ${inr(banked.settledINR)} banked` : ''}
            </>
          )}
        </span>
      </div>
    </div>
  );
}

function ProjectRow({
  m,
  live,
  actorName,
  settled,
  onRecord,
  onCollapse,
}: {
  m: ProjectMoney;
  live: LiveProject | null;
  actorName: string;
  settled: SettledTotals | null;
  onRecord: () => void;
  /** UX C — close the detail and return to the shortlist. */
  onCollapse?: () => void;
}) {
  const { toast } = useToast();
  const [removing, setRemoving] = useState(false);
  const title = `${m.vp} — ${m.clientName}`;
  const banked = m.currency === 'USD' && settled && settled.settledINR > 0
    ? settled
    : null;
  const note = m.unpriced
    ? 'not yet priced'
    : `${money(m.receivedINR, m.currency)} received · ${money(
        m.balanceINR,
        m.currency,
      )} outstanding${banked ? ` · ${inr(banked.settledINR)} banked` : ''}`;
  const hasDue = m.gates.some((g) => !g.cleared);

  async function removeFromPayments() {
    setRemoving(true);
    try {
      await setProjectTrackedInPayments(m.pid, false, actorName);
      toast(`Removed ${m.vp} from Payments (the project is untouched).`, {
        ok: true,
      });
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Could not remove.', {});
    } finally {
      setRemoving(false);
    }
  }

  return (
    <Box title={title} note={note}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {onCollapse && (
            <Btn3 intent="ghost" size="sm" onClick={onCollapse} title="Back to the engagement list">
              ← Back to list
            </Btn3>
          )}
          <FirmChip firmId={m.firmId} label={m.firmShort} />
          {m.firmOverridden && <Tag kind="warn" title="Firm manually overridden">override</Tag>}
          <Tag kind="tag">{m.currency}</Tag>
          {m.projectName && m.projectName !== '—' && (
            <span style={{ fontSize: 12, color: 'var(--ink-mute)' }}>
              {m.projectName}
            </span>
          )}
          <span style={{ marginLeft: 'auto', fontSize: 13, color: 'var(--ink-soft)' }}>
            value {money(m.valueINR, m.currency)}
          </span>
          {!m.unpriced && hasDue && (
            <Btn3 intent="grn" size="sm" onClick={onRecord}>
              Record payment
            </Btn3>
          )}
          <Btn3
            intent="ghost"
            size="sm"
            disabled={removing}
            onClick={removeFromPayments}
            title="Stop tracking this engagement in Payments (the project is not deleted)"
          >
            {removing ? 'Removing…' : 'Remove'}
          </Btn3>
        </div>

        {/* AGREED PRICE — set/edit the negotiated fee on this LIVE engagement
            (the $40 / Tarveen fix), here on the PAYMENT tab (owner ruling
            2026-06-15). Writes gateAmounts → the gate amounts below + on the
            project page + in record-payment all expect the agreed figure. */}
        {live && (
          <AgreedPricePanel
            live={live}
            paid={m.receivedINR}
            actorName={actorName}
            onMsg={(msg) => toast(msg, { ok: true })}
          />
        )}

        {m.unpriced ? (
          live ? (
            <MoneySetup live={live} />
          ) : (
            <EmptyNote title="Tier / value not yet confirmed">
              This engagement has no priced gates yet.
            </EmptyNote>
          )
        ) : (
          <>
            {m.gates.map((g) => (
              <Card key={g.id} flat className="row-card">
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    flexWrap: 'wrap',
                  }}
                >
                  {/* gate amount is in the ENGAGEMENT currency ($ for a USD
                      engagement) — never force ₹ on a USD gate. */}
                  <b>{money(g.amountINR, m.currency)}</b>
                  <span style={{ fontSize: 13, color: 'var(--ink-soft)' }}>
                    {g.label}
                  </span>
                  <span style={{ marginLeft: 'auto' }}>
                    {g.cleared ? (
                      <Tag>received</Tag>
                    ) : (
                      <Tag kind="warn">due — before delivery</Tag>
                    )}
                  </span>
                </div>
              </Card>
            ))}
            {banked && (
              <div style={{ fontSize: 12, color: 'var(--ink-soft)', paddingTop: 2 }}>
                Bank settlement:{' '}
                <b style={{ color: 'var(--ink)' }}>{inr(banked.settledINR)}</b>{' '}
                credited in INR for{' '}
                <b>{money(banked.receivedUSD, 'USD')}</b> received
                {banked.receivedUSD > 0 &&
                  ` · effective ₹${(banked.settledINR / banked.receivedUSD).toFixed(2)}/$`}
                .
              </div>
            )}
          </>
        )}
      </div>
    </Box>
  );
}

/**
 * Minimal admin control to stamp a project's currency + value (and the GST
 * flag) so its money figures become exact. Writes via `stampProjectMoney`
 * (which also flips `trackedInPayments:true`). The live listener re-renders
 * the row priced once the stamp lands.
 */
function MoneySetup({ live }: { live: LiveProject }) {
  const { toast } = useToast();
  const [currency, setCurrency] = useState<Currency>(live.currency);
  const [valueStr, setValueStr] = useState(
    live.valueINR ? String(live.valueINR) : '',
  );
  const [gst, setGst] = useState<boolean>(
    live.gst != null ? live.gst : live.currency !== 'USD',
  );
  const [saving, setSaving] = useState(false);
  const value = Number(valueStr.replace(/[^0-9.]/g, '')) || 0;

  async function save() {
    setSaving(true);
    try {
      await stampProjectMoney(
        live.pid,
        { currency, valueINR: value, gst },
        'Payments setup',
      );
      toast('Engagement priced — figures now exact.', { ok: true });
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Could not save.', {});
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card flat>
      <div style={{ fontSize: 12.5, color: 'var(--ink-soft)', marginBottom: 10 }}>
        Not yet priced. Set the currency &amp; engagement value so the payment
        schedule (from the locked gates) resolves and the figures are exact.
      </div>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <label style={{ fontSize: 12, color: 'var(--ink-soft)' }}>
          Currency
          <select
            value={currency}
            onChange={(e) => {
              const c = e.target.value as Currency;
              setCurrency(c);
              if (c === 'USD') setGst(false);
            }}
            style={{
              display: 'block',
              marginTop: 4,
              padding: '8px 10px',
              borderRadius: 'var(--r-sm)',
              border: '1px solid var(--rule-strong)',
              fontFamily: 'inherit',
            }}
          >
            <option value="INR">INR (₹)</option>
            <option value="USD">USD ($)</option>
          </select>
        </label>
        <label style={{ fontSize: 12, color: 'var(--ink-soft)' }}>
          Engagement value ({currency === 'USD' ? '$' : '₹'})
          <input
            value={valueStr}
            onChange={(e) => setValueStr(e.target.value)}
            inputMode="decimal"
            placeholder="0"
            style={{
              display: 'block',
              marginTop: 4,
              padding: '8px 10px',
              borderRadius: 'var(--r-sm)',
              border: '1px solid var(--rule-strong)',
              fontFamily: 'inherit',
              width: 150,
            }}
          />
        </label>
        {currency === 'INR' && (
          <label
            style={{
              fontSize: 12,
              color: 'var(--ink-soft)',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              paddingBottom: 8,
            }}
          >
            <input
              type="checkbox"
              checked={gst}
              onChange={(e) => setGst(e.target.checked)}
            />
            GST-billed (→ Aayatan Veda)
          </label>
        )}
        <Btn3 intent="ink" size="sm" disabled={saving} onClick={save}>
          {saving ? 'Saving…' : 'Save'}
        </Btn3>
      </div>
    </Card>
  );
}

/** A small firm chip (matches the v3 mockup's firm-chip). */
function FirmChip({ firmId, label }: { firmId: string; label: string }) {
  const isAv = firmId === 'firm1';
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        fontSize: 12,
        fontWeight: 600,
        padding: '2px 9px',
        borderRadius: 999,
        border: `1px solid ${isAv ? 'var(--accent-soft)' : 'var(--rule-strong)'}`,
        background: isAv ? 'var(--accent-wash)' : 'var(--inset)',
        color: isAv ? 'var(--accent-deep)' : 'var(--ink-soft)',
      }}
    >
      <span
        aria-hidden
        style={{
          width: 7,
          height: 7,
          borderRadius: 999,
          background: isAv ? 'var(--accent)' : 'var(--ink-mute)',
        }}
      />
      {label}
    </span>
  );
}

function CashBookLedger({
  tracked,
  actorName,
}: {
  tracked: LiveProject[];
  actorName: string;
}) {
  const { toast } = useToast();
  const [rows, setRows] = useState<CashBookEntry[] | null>(null);
  const [q, setQ] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const [edit, setEdit] = useState<{ amount: string; uiMethod: string; reference: string; settledINR: string } | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const unsub = listenCashBook(setRows, () => setRows([]));
    return unsub;
  }, []);

  const labelOf = (pid: string): string => {
    const p = tracked.find((x) => x.pid === pid);
    return p ? `${p.vp} · ${p.clientName}` : pid || '(no engagement)';
  };

  if (rows === null) {
    return (
      <Box title="Cash book" note="every payment · newest first · click a row to edit">
        <EmptyNote title="Loading the ledger…">Reading payments.</EmptyNote>
      </Box>
    );
  }

  const shown = searchCashBook(rows, q, labelOf);

  function openRow(r: CashBookEntry) {
    if (openId === r.id) {
      setOpenId(null);
      setEdit(null);
      return;
    }
    setOpenId(r.id);
    setEdit({
      amount: String(r.amountINR || ''),
      uiMethod: r.uiMethod || 'upi',
      reference: r.reference,
      settledINR: r.amountSettledINR ? String(r.amountSettledINR) : '',
    });
  }

  async function saveRow(r: CashBookEntry) {
    if (!edit || saving) return;
    setSaving(true);
    try {
      await updateCashBookEntry(
        r,
        {
          amount: Number(edit.amount),
          uiMethod: edit.uiMethod,
          reference: edit.reference,
          settledINR: edit.settledINR.trim() === '' ? null : Number(edit.settledINR),
        },
        actorName,
      );
      toast('Entry updated.', { ok: true });
      setOpenId(null);
      setEdit(null);
    } catch (e) {
      toast(`Edit failed: ${(e as Error).message}`, {});
    } finally {
      setSaving(false);
    }
  }

  const fmtDate = (ms: number) =>
    ms
      ? new Date(ms).toLocaleDateString([], { day: 'numeric', month: 'short', year: '2-digit' })
      : '—';

  return (
    <Box title="Cash book" note="every payment · newest first · click a row to open & edit">
      {rows.length === 0 ? (
        <EmptyNote title="No payments recorded yet">
          Receipts land here as you record them (“+ Record payment”).
        </EmptyNote>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {rows.length > 8 && (
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search the cash book — VP, client, gate, reference, method…"
              aria-label="Search the cash book"
              style={{
                width: '100%',
                padding: '9px 12px',
                borderRadius: 'var(--r-sm)',
                border: '1px solid var(--rule-strong)',
                fontFamily: 'inherit',
                fontSize: 14,
                boxSizing: 'border-box',
                marginBottom: 6,
              }}
            />
          )}
          {shown.length === 0 && (
            <EmptyNote title="No ledger matches">
              Nothing matches “{q}”. Clear the search to see all {rows.length} entries.
            </EmptyNote>
          )}
          {shown.map((r) => {
            const isOpen = openId === r.id;
            return (
              <Card key={r.id} flat className="row-card" style={{ padding: 0 }}>
                <button
                  type="button"
                  onClick={() => openRow(r)}
                  aria-expanded={isOpen}
                  style={{
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: 10,
                    width: '100%',
                    textAlign: 'left',
                    background: 'transparent',
                    border: 'none',
                    fontFamily: 'inherit',
                    cursor: 'pointer',
                    padding: '8px 12px',
                    fontSize: 12.5,
                  }}
                >
                  <span style={{ color: 'var(--ink-mute)', whiteSpace: 'nowrap', width: 74, flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>
                    {fmtDate(r.atMs)}
                  </span>
                  <span style={{ fontWeight: 600, color: 'var(--ink)', flex: '1 1 180px', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {labelOf(r.projectId)}
                  </span>
                  <span style={{ color: 'var(--ink-soft)', flex: '1 1 140px', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {r.gateId || 'payment'}
                    {r.reference ? ` · ${r.reference}` : ''}
                  </span>
                  <Tag kind={r.partial ? 'warn' : 'val'}>{r.partial ? 'partial' : 'cleared'}</Tag>
                  <span style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', color: 'var(--ink)' }}>
                    {money(r.amountINR, r.currency)}
                  </span>
                  {r.currency === 'USD' && r.amountSettledINR > 0 && (
                    <span style={{ fontSize: 11.5, color: 'var(--ink-mute)', whiteSpace: 'nowrap' }}>
                      {inr(r.amountSettledINR)} banked
                    </span>
                  )}
                </button>
                {isOpen && edit && (
                  <div style={{ padding: '0 12px 12px', borderTop: '1px solid var(--rule)' }}>
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 10 }}>
                      <label style={{ fontSize: 12, color: 'var(--ink-soft)', flex: '0 1 140px' }}>
                        Amount ({r.currency})
                        <input
                          value={edit.amount}
                          onChange={(e) => setEdit((p) => (p ? { ...p, amount: e.target.value.replace(/[^\d.]/g, '') } : p))}
                          inputMode="decimal"
                          style={{ width: '100%', marginTop: 4, padding: '8px 10px', border: '1px solid var(--rule-strong)', borderRadius: 'var(--r-sm)', fontFamily: 'inherit', fontSize: 13.5, boxSizing: 'border-box' }}
                        />
                      </label>
                      <label style={{ fontSize: 12, color: 'var(--ink-soft)', flex: '0 1 150px' }}>
                        Method
                        <select
                          value={edit.uiMethod}
                          onChange={(e) => setEdit((p) => (p ? { ...p, uiMethod: e.target.value } : p))}
                          style={{ width: '100%', marginTop: 4, padding: '8px 10px', border: '1px solid var(--rule-strong)', borderRadius: 'var(--r-sm)', fontFamily: 'inherit', fontSize: 13.5, boxSizing: 'border-box' }}
                        >
                          {/* Vera MEDIUM-3 (2026-07-04): the REAL per-currency
                              method vocabulary (recordPayment.methodsFor /
                              methodLabel) — the hand-rolled upi/bank/cash/
                              card/'other' list couldn't represent recorded
                              cheque/wire/paypal/stripe rows ('other' isn't a
                              UiMethod at all). A legacy value outside the set
                              is kept selectable so the row round-trips. */}
                          {(methodsFor(r.currency) as string[])
                            .concat(
                              edit.uiMethod && !(methodsFor(r.currency) as string[]).includes(edit.uiMethod)
                                ? [edit.uiMethod]
                                : [],
                            )
                            .map((m) => (
                              <option key={m} value={m}>
                                {methodLabel(m as UiMethod) || m}
                              </option>
                            ))}
                        </select>
                      </label>
                      <label style={{ fontSize: 12, color: 'var(--ink-soft)', flex: '2 1 200px' }}>
                        Reference / note
                        <input
                          value={edit.reference}
                          onChange={(e) => setEdit((p) => (p ? { ...p, reference: e.target.value } : p))}
                          style={{ width: '100%', marginTop: 4, padding: '8px 10px', border: '1px solid var(--rule-strong)', borderRadius: 'var(--r-sm)', fontFamily: 'inherit', fontSize: 13.5, boxSizing: 'border-box' }}
                        />
                      </label>
                      {r.currency === 'USD' && (
                        <label style={{ fontSize: 12, color: 'var(--ink-soft)', flex: '0 1 150px' }}>
                          INR banked
                          <input
                            value={edit.settledINR}
                            onChange={(e) => setEdit((p) => (p ? { ...p, settledINR: e.target.value.replace(/[^\d]/g, '') } : p))}
                            inputMode="numeric"
                            style={{ width: '100%', marginTop: 4, padding: '8px 10px', border: '1px solid var(--rule-strong)', borderRadius: 'var(--r-sm)', fontFamily: 'inherit', fontSize: 13.5, boxSizing: 'border-box' }}
                          />
                        </label>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 10, flexWrap: 'wrap' }}>
                      <Btn3 intent="grn" size="sm" onClick={() => void saveRow(r)} disabled={saving}>
                        {saving ? 'Saving…' : 'Save entry'}
                      </Btn3>
                      <Btn3 intent="ghost" size="sm" onClick={() => { setOpenId(null); setEdit(null); }} disabled={saving}>
                        Cancel
                      </Btn3>
                      <span style={{ fontSize: 11.5, color: 'var(--ink-mute)' }}>
                        Edits fix the bookkeeping record only — they don't re-run
                        the money gate. A new receipt goes through “+ Record payment”.
                      </span>
                    </div>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </Box>
  );
}
